import { useState } from 'react';
import { 
  AppBar, 
  Avatar,
  Box, 
  Button, 
  Container, 
  Divider,
  Drawer,
  IconButton, 
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar, 
  Tooltip,
  Typography, 
  useMediaQuery,
  useTheme as useMuiTheme 
} from '@mui/material';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import InventoryIcon from '@mui/icons-material/Inventory';
import PeopleIcon from '@mui/icons-material/People';

const drawerWidth = 240;

const ownerNavItems = [
  { name: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { name: 'Transactions', path: '/transactions', icon: <ReceiptIcon /> },
  { name: 'Invoices', path: '/invoices', icon: <DescriptionIcon /> },
  { name: 'Employees', path: '/employees', icon: <PeopleIcon /> },
];

const employeeNavItems = [
  { name: 'Sales', path: '/sales', icon: <PointOfSaleIcon /> },
  { name: 'Past Sales', path: '/past-sales', icon: <ReceiptIcon /> },
  { name: 'Expenses', path: '/expenses', icon: <DescriptionIcon /> },
  { name: 'Items', path: '/items', icon: <InventoryIcon /> },
];

export function Layout() {
  const { user, isOwner, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const { mode, toggleTheme } = useTheme();
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  const userInitial = user?.email?.charAt(0).toUpperCase() || 'U';

  const drawer = (
    <Box sx={{ overflow: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 3,
        }}
      >
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: 'primary.main',
            mb: 1,
            fontWeight: 'bold',
          }}
        >
          {userInitial}
        </Avatar>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {user?.email?.split('@')[0]}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isOwner ? 'Owner' : 'Employee'}
        </Typography>
      </Box>
      
      <Divider />
      
      <List>
        {(isOwner ? ownerNavItems : employeeNavItems).map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <ListItem key={item.name} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive}
                sx={{
                  borderLeft: isActive ? 3 : 0,
                  borderColor: 'primary.main',
                  '&.Mui-selected': {
                    bgcolor: mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&:hover': {
                    bgcolor: mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.12)' 
                      : 'rgba(0, 0, 0, 0.06)',
                  },
                  pl: isActive ? 1.7 : 2,
                }}
                onClick={() => {
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isActive ? 'primary.main' : 'text.secondary',
                    minWidth: 45
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.name}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      
      <Divider sx={{ mt: 2, mb: 2 }} />
      
      <Box sx={{ px: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleSignOut}
          sx={{
            justifyContent: 'flex-start',
            py: 1,
          }}
        >
          Sign Out
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            Theme Mode
          </Typography>
          <IconButton onClick={toggleTheme} color="inherit" size="small">
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(45deg, #1a237e 30%, #311b92 90%)'
            : '#ffffff',
          color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1, // Ensure AppBar is above drawer on mobile
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {(isOwner ? ownerNavItems : employeeNavItems).find(item => item.path === location.pathname)?.name || (isOwner ? 'Dashboard' : 'Sales')}
          </Typography>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Toggle theme">
                <IconButton onClick={toggleTheme} color="inherit" sx={{ ml: 2 }}>
                  {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Tooltip>
              
              <Tooltip title={user?.email || 'User'}>
                <IconButton color="inherit" sx={{ ml: 1 }}>
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          container={window.document.body}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
            <IconButton onClick={handleDrawerToggle}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center' }}>
              CRM {isOwner ? 'Owner' : 'Portal'}
            </Typography>
          </Box>
          <Divider />
          {drawer}
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography 
              variant="h5" 
              component="div"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.5,
                background: 'linear-gradient(45deg, #3f51b5 30%, #673ab7 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              CRM {isOwner ? 'Owner' : 'Portal'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Business Management Portal
            </Typography>
          </Box>
          <Divider />
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          p: { xs: 1, sm: 2, md: 3 }, // Reduced padding on mobile
          pt: { xs: 9, sm: 10, md: 11 }, // Adjusted top padding for smaller screens
          bgcolor: 'background.default',
          minHeight: '100vh',
          overflow: 'hidden', // Prevent horizontal scrolling
        }}
      >
        <Container disableGutters maxWidth="xl">
          <Box className="fade-in" sx={{ width: '100%', overflowX: 'hidden' }}>
            <Outlet />
          </Box>
        </Container>
        
        <Box
          component="footer"
          sx={{
            py: { xs: 2, md: 3 },
            mt: 'auto',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} CRM {isOwner ? 'Owner Dashboard' : 'Portal'} • All rights reserved
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}